import React, { useMemo } from 'react';
import { Stack, styled, Typography, Button, Grid2 } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import {
  RESOURCE_LAYOUT_GRID_COLUMNS,
  RESOURCE_SECTION_MAX_WIDTH,
  RESOURCE_SECTION_PADDING,
} from 'themes/constants';

interface ResourceSectionProps {
  title: string;
  children: React.ReactNode;
  titleId?: string;
  description?: string;
  viewAllText?: string;
  viewAllAriaLabel?: string;
  onViewAll?: () => void;
  showViewAll?: boolean;
  headerAction?: React.ReactNode;
}

export const ResourceGridContainer = styled(Grid2)(({ theme }) => ({
  width: '100%',
  maxWidth: RESOURCE_SECTION_MAX_WIDTH.xs,
  // Cards are fixed-width, so once maxWidth caps the row, center it instead of leaving the
  // leftover space on the right.
  margin: '0 auto',
  paddingInline: theme.spacing(RESOURCE_SECTION_PADDING.xs),
  [theme.breakpoints.up('sm')]: {
    maxWidth: RESOURCE_SECTION_MAX_WIDTH.sm,
    paddingInline: theme.spacing(RESOURCE_SECTION_PADDING.sm),
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: RESOURCE_SECTION_MAX_WIDTH.md,
    paddingInline: theme.spacing(RESOURCE_SECTION_PADDING.md),
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: RESOURCE_SECTION_MAX_WIDTH.lg,
  },
}));

const HeaderGrid2Section = styled(Grid2)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const MobileButtonGrid2Section = styled(Grid2)(() => ({
  textAlign: 'center',
}));

const ViewAllButton = styled(Button)(() => ({
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '16px',
  lineHeight: '21px',
}));

export const ResourceSection: React.FC<ResourceSectionProps> = ({
  title,
  children,
  titleId,
  description,
  viewAllText = 'View all',
  viewAllAriaLabel,
  onViewAll,
  headerAction,
  showViewAll = true,
}) => {
  const { isMobile, isTablet } = useDeviceType();

  const getTitleVariant = useMemo(() => {
    if (isMobile) return 'callout';
    if (isTablet) return 'headline';
    return 'title3';
  }, [isMobile, isTablet]);

  const descriptionId = titleId ? `${titleId}-description` : undefined;

  const viewAllLink = (
    <ViewAllButton
      variant="text"
      endIcon={<ChevronRight />}
      color="primary"
      onClick={onViewAll}
      aria-label={viewAllAriaLabel || `${viewAllText} for ${title}`}
    >
      {viewAllText}
    </ViewAllButton>
  );

  return (
    <ResourceGridContainer
      container
      spacing={2}
      columns={RESOURCE_LAYOUT_GRID_COLUMNS}
      role="region"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <HeaderGrid2Section size={RESOURCE_LAYOUT_GRID_COLUMNS}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="column" spacing={1}>
            <Typography fontWeight={500} color="grey.800" variant={getTitleVariant} id={titleId}>
              {title}
            </Typography>
            <Typography variant="callout" color="grey.700" id={descriptionId}>
              {description}
            </Typography>
          </Stack>
          {!isMobile && (headerAction ?? (showViewAll ? viewAllLink : null))}
        </Stack>
      </HeaderGrid2Section>
      {children}
      {isMobile && !headerAction && showViewAll && (
        <MobileButtonGrid2Section size={RESOURCE_LAYOUT_GRID_COLUMNS}>
          {viewAllLink}
        </MobileButtonGrid2Section>
      )}
    </ResourceGridContainer>
  );
};
